// src/views/app-views/acquisition/leads/index.js
import React, { useEffect, useRef, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Drawer,
  Dropdown,
  Form,
  Input,
  Menu,
  message,
  Modal,
  Popconfirm,
  Popover,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Timeline,
  Typography,
  Upload,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  FilterOutlined,
  SearchOutlined,
  UserSwitchOutlined,
} from "@ant-design/icons";
import EllipsisDropdown from "components/shared-components/EllipsisDropdown";
import Flex from "components/shared-components/Flex";
import utils from "utils";
import { useTranslation } from "react-i18next";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  increment,
  query,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { firestore, storage } from "../../../../configs/FirebaseConfig";
import { deleteObject, ref } from "firebase/storage";
import dayjs from "dayjs";
import { dummyRequest, isMobile } from "../../../../functions/browser";
import {
  FIRESTORE_CHAT_SESSIONS_TABLE,
  FIRESTORE_LEADS_TABLE,
  FIRESTORE_ORDERS_TABLE,
  FIRESTORE_REGIONS_TABLE,
} from "../../../../constants/FirestoreConstant";
import { COMPANY_COUNTRY_CODE } from "../../../../constants/CompanyConstants";
import AddLeadModal from "./modals/AddLeadModal";
import AddCompanyModal from "../../customers/companies/modals/AddCompanyModal";
import { useLeads } from "../../../../data/hooks/firestore/useLeads";
import { useAuth } from "../../../../data/hooks/firebase/useAuth";
import { useCountries } from "../../../../data/hooks/firestore/useCountries";
import { useProducts } from "../../../../data/hooks/firestore/useProducts";
import { ORDER_STATUS_CANCELED } from "../../../../constants/OrderConstant";
import roles from "../../../../roles";
import { StyledSelect } from "../../../../styles/sharedStyles";
import {
  LEAD_STATUS_ACTION_NEEDED,
  LEAD_STATUS_ALMOST_IN,
  LEAD_STATUS_FOLLOW_UP,
  LEAD_STATUS_INTERESTED,
  LEAD_STATUS_MANUAL,
  LEAD_STATUS_NEW,
  LEAD_STATUS_NO_ANSWER,
  LEAD_STATUS_NOT_INTERESTED,
  LEAD_STATUS_OVERDUE,
  LEAD_STATUS_READY,
  LEAD_STATUS_TRIAL_SENT,
  LEAD_STATUSES,
} from "../../../../constants/LeadConstant";
import styled from "styled-components";
import { parseExcelLeads } from "../../../../utils/excelLeadsUpload";
import PreviewExcelLeadsModal from "./modals/PreviewExcelLeadsModal";
import PreviewBulkShipmentsModal from "./modals/PreviewBulkShipmentsModal";
import useIsMobile from "../../../../hooks/useIsMobile";
import { DeliveryZoneBadge } from "../../../../functions/deliveryZones";

const { Dragger } = Upload;

// Template configuration (should match onLeadCreate function)
const LEAD_INTRO_TEMPLATE = {
  id: "template_1_nl",
  name: "Proefpakket bevestiging",
  language: "en",
  getVariables: (lead) => {
    const contactName = lead.contactFirstName || lead.companyName;
    const street = lead.street || "";
    const houseNumber = lead.houseNumber || "";
    const houseNumberAddition = lead.houseNumberAddition || "";
    const zipCode = lead.zipCode || "";
    const city = lead.city || "";

    const variables = {
      1: contactName,
      2: street,
      3: `${houseNumber}${houseNumberAddition}`,
      4: zipCode,
      5: city,
    };
    const variablesArray = [contactName, street, `${houseNumber}${houseNumberAddition}`, zipCode, city];
    return { variables, variablesArray };
  },
  render: (variables) => {
    return `Hi ${variables[1]}!

Dank voor het aanvragen van een proefpakket. Wij versturen deze van dinsdag t/m zaterdag en hij wordt afgeleverd tussen 10:00 en 18:00.

Klopt het dat uw adres ${variables[2]} ${variables[3]}, ${variables[4]} in ${variables[5]} is?

En welke dag zou u het proefpakket willen ontvangen?

Groet,

Twan
Chef's Farm`;
  },
};

const Leads = () => {
  const { leads, isLoading: leadsLoading } = useLeads();
  const { profile } = useAuth();
  const { getCountryDeliveryLabels, getCountryDelivery } = useCountries();
  const { products } = useProducts();
  const [filteredLeads, setFilteredLeads] = useState(leads);
  const [searchValue, setSearchValue] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [desiredDeliveryDateFilter, setDesiredDeliveryDateFilter] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState("all");
  const [hasDeliverySettings, setHasDeliverySettings] = useState("all");
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [addLeadModalVisible, setAddLeadModalVisible] = useState(false);
  const [editLeadModalVisible, setEditLeadModalVisible] = useState(false);
  const [editFollowUpModalVisible, setEditFollowUpModalVisible] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadOrder, setLeadOrder] = useState(null);
  const [loadingLeadOrder, setLoadingLeadOrder] = useState(false);
  const { t } = useTranslation();
  const [isDraggingExcelFile, setIsDraggingExcelFile] = useState(false);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [previewValidLeads, setPreviewValidLeads] = useState([]);
  const [previewInvalidLeads, setPreviewInvalidLeads] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [filterPopoverVisible, setFilterPopoverVisible] = useState(false);
  const isMobileView = useIsMobile();
  const [bulkDeliveryDateModalVisible, setBulkDeliveryDateModalVisible] = useState(false);
  const [bulkDeliverySettingsModalVisible, setBulkDeliverySettingsModalVisible] = useState(false);
  const [selectedLeadsCountry, setSelectedLeadsCountry] = useState(null);
  const [bulkShipmentsModalVisible, setBulkShipmentsModalVisible] = useState(false);
  const [bulkShipmentsLeads, setBulkShipmentsLeads] = useState([]);
  const [convertToCustomerModalVisible, setConvertToCustomerModalVisible] = useState(false);
  const [leadToConvert, setLeadToConvert] = useState(null);
  const leadToConvertRef = useRef(null);
  const [hasShipmentFilter, setHasShipmentFilter] = useState("all");
  const [leadsWithShipments, setLeadsWithShipments] = useState(new Set());

  const [editFollowUpForm] = Form.useForm();
  const [bulkDeliveryDateForm] = Form.useForm();
  const [bulkDeliverySettingsForm] = Form.useForm();

  const deliveryLabelWatch = Form.useWatch("deliveryLabel", bulkDeliverySettingsForm);

  useEffect(() => {
    if (deliveryLabelWatch) {
      const availableTimeSlots = getAvailableTimeSlotsForLabel(deliveryLabelWatch);

      if (availableTimeSlots.length === 1) {
        bulkDeliverySettingsForm.setFieldsValue({ deliveryTimeSlot: availableTimeSlots[0].value });
      } else {
        bulkDeliverySettingsForm.setFieldsValue({ deliveryTimeSlot: null });
      }
    }
  }, [deliveryLabelWatch, bulkDeliverySettingsForm]);

  const getLeadDesiredDeliveryDateDay = (lead) => {
    const value = lead?.desiredDeliveryDate;
    if (!value) return null;

    // Firestore Timestamp (or timestamp-like)
    if (typeof value === "object" && typeof value.seconds === "number") {
      return dayjs.unix(value.seconds).startOf("day");
    }

    // unix seconds OR milliseconds
    if (typeof value === "number") {
      const asSeconds = value > 1e12 ? Math.floor(value / 1000) : value;
      return dayjs.unix(asSeconds).startOf("day");
    }

    // ISO/date string (e.g. 2026-03-05)
    if (typeof value === "string") {
      const parsed = dayjs(value);
      return parsed.isValid() ? parsed.startOf("day") : null;
    }

    return null;
  };

  const getLeadCreatedAtSeconds = (lead) => {
    const createdAt = lead?.createdAt;
    if (!createdAt) return null;
    if (typeof createdAt === "number") {
      return createdAt > 1e12 ? Math.floor(createdAt / 1000) : createdAt;
    }
    if (typeof createdAt === "object" && typeof createdAt.seconds === "number") {
      return createdAt.seconds;
    }
    return null;
  };

  const getDeliveryLabelName = (deliveryLabelCode, countryCode) => {
    if (!deliveryLabelCode) return null;
    const labels = getCountryDeliveryLabels(countryCode);
    const label = labels.find((l) => l.code === deliveryLabelCode);
    return label?.label || deliveryLabelCode;
  };

  const formatTimeSlot = (timeSlot) => {
    if (!timeSlot) return null;
    const { start, end, deliverySurcharge } = timeSlot;
    let formatted = `${start} - ${end}`;
    if (deliverySurcharge && deliverySurcharge > 0) {
      formatted += ` (+€${deliverySurcharge})`;
    }
    return formatted;
  };

  // Load existing shipments data
  useEffect(() => {
    const loadExistingShipments = async () => {
      try {
        const ordersQuery = query(
          collection(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_ORDERS_TABLE),
          where("type", "==", "acquisition"),
        );

        const ordersSnapshot = await getDocs(ordersQuery);
        const leadIdsWithShipments = new Set();

        ordersSnapshot.docs.forEach((doc) => {
          const orderData = doc.data();
          // Only count non-cancelled shipments
          if (orderData.leadId && orderData.farmorderStatus !== ORDER_STATUS_CANCELED) {
            leadIdsWithShipments.add(orderData.leadId);
          }
        });

        setLeadsWithShipments(leadIdsWithShipments);
      } catch (error) {
        console.error("Error loading existing shipments:", error);
      }
    };

    loadExistingShipments();
  }, []);

  useEffect(() => {
    if (leads) {
      applyFilters(
        searchValue,
        selectedStatus,
        desiredDeliveryDateFilter,
        selectedCountry,
        hasDeliverySettings,
        hasShipmentFilter,
      );
    }
  }, [
    leads,
    selectedStatus,
    desiredDeliveryDateFilter,
    selectedCountry,
    hasDeliverySettings,
    hasShipmentFilter,
    leadsWithShipments,
  ]);

  const showDrawer = async (lead) => {
    setDrawerVisible(true);
    setSelectedLead(lead);
    setLeadOrder(null);
    setLoadingLeadOrder(true);

    try {
      // Query for farmorder with this leadId
      const ordersQuery = query(
        collection(firestore, `${FIRESTORE_REGIONS_TABLE}/${COMPANY_COUNTRY_CODE}/${FIRESTORE_ORDERS_TABLE}`),
        where("leadId", "==", lead.leadId),
      );

      const querySnapshot = await getDocs(ordersQuery);

      // Filter out canceled orders
      const nonCanceledOrders = querySnapshot.docs
        .map((doc) => ({ ...doc.data(), id: doc.id }))
        .filter((order) => order.farmorderStatus !== ORDER_STATUS_CANCELED);

      if (nonCanceledOrders.length > 0) {
        setLeadOrder(nonCanceledOrders[0]);
      }
    } catch (error) {
      console.error("Error fetching lead order:", error);
      message.error(t("notifications.error_fetching_order"));
    } finally {
      setLoadingLeadOrder(false);
    }
  };

  const closeDrawer = () => {
    setDrawerVisible(false);
    setSelectedLead(null);
    setLeadOrder(null);
    setEditFollowUpModalVisible(false);
  };

  const openEditFollowUpModal = () => {
    if (!selectedLead) return;

    const desiredDeliveryDate = getLeadDesiredDeliveryDateDay(selectedLead);
    editFollowUpForm.setFieldsValue({
      desiredDeliveryDate: desiredDeliveryDate ? desiredDeliveryDate : null,
    });

    setEditFollowUpModalVisible(true);
  };

  const closeEditFollowUpModal = () => {
    setEditFollowUpModalVisible(false);
    editFollowUpForm.resetFields();
  };

  const handleSaveFollowUpInformation = async () => {
    if (!selectedLead) return;

    try {
      const values = await editFollowUpForm.validateFields();
      const desiredDeliveryDate = values?.desiredDeliveryDate
        ? dayjs(values.desiredDeliveryDate).startOf("day").format("YYYY-MM-DD")
        : null;

      const leadDocRef = doc(
        firestore,
        FIRESTORE_REGIONS_TABLE,
        COMPANY_COUNTRY_CODE,
        FIRESTORE_LEADS_TABLE,
        selectedLead.id,
      );

      await updateDoc(leadDocRef, {
        desiredDeliveryDate: desiredDeliveryDate,
      });

      const nextLead = {
        ...selectedLead,
        desiredDeliveryDate: desiredDeliveryDate,
      };

      setSelectedLead(nextLead);
      setFilteredLeads((prev) => prev.map((l) => (l.id === selectedLead.id ? { ...l, ...nextLead } : l)));

      message.success(t("notifications.follow_up_information_updated"));
      closeEditFollowUpModal();
    } catch (error) {
      if (error?.errorFields) {
        return;
      }
      console.error("Error updating follow up information:", error);
      message.error(t("notifications.error_saving_follow_up_information"));
    }
  };

  const onPressEdit = (lead) => {
    setSelectedLead(lead);
    setEditLeadModalVisible(true);
  };

  const onPressConvertToCustomer = (lead) => {
    leadToConvertRef.current = lead;
    setLeadToConvert(lead);
    setConvertToCustomerModalVisible(true);
  };

  const closeConvertToCustomerModal = () => {
    setConvertToCustomerModalVisible(false);
    setLeadToConvert(null);
    leadToConvertRef.current = null;
  };

  const handleRemoveLeadDeliveryInstructions = async () => {
    if (!selectedLead?.id) return;
    try {
      if (selectedLead.deliveryInstructionsPath) {
        await deleteObject(ref(storage, selectedLead.deliveryInstructionsPath));
      } else if (selectedLead.deliveryInstructionsUrl) {
        await deleteObject(ref(storage, selectedLead.deliveryInstructionsUrl));
      }
    } catch (e) {
      console.error(e);
    }

    try {
      await updateDoc(
        doc(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_LEADS_TABLE, selectedLead.id),
        {
          deliveryInstructionsUrl: null,
          deliveryInstructionsPath: null,
          updatedAt: dayjs().unix(),
          updatedBy: profile?.email || "unknown",
        },
      );

      // Update local state
      setSelectedLead({
        ...selectedLead,
        deliveryInstructionsUrl: null,
        deliveryInstructionsPath: null,
      });

      message.success("Delivery Instructions removed");
    } catch (e) {
      console.error(e);
      message.error("Failed to remove Delivery Instructions");
    }
  };

  const onConvertSuccess = () => {
    const convertedLead = leadToConvertRef.current;
    closeConvertToCustomerModal();
    if (!convertedLead) return;
    setTimeout(() => {
      Modal.confirm({
        title: t("delete_lead_after_conversion_title"),
        content: t("delete_lead_after_conversion_message", { name: convertedLead.companyName }),
        okText: t("yes"),
        cancelText: t("no"),
        onOk: () => deleteRow(convertedLead),
      });
    }, 300);
  };

  const buildCustomerPrefillFromLead = (lead) => {
    if (!lead) return null;
    return {
      customerName: lead.companyName,
      contact: {
        firstName: lead.contactFirstName || "",
        lastName: lead.contactLastName || "",
      },
      customerEmail: lead.email || "",
      customerPhone: lead.phone || "",
      street: lead.street || "",
      houseNumber: lead.houseNumber || "",
      houseNumberAddition: lead.houseNumberAddition || "",
      zipCode: lead.zipCode || "",
      city: lead.city || "",
      country: lead.country || "NL",
      deliveryLabel: lead.deliveryLabel || null,
      deliveryTimeSlot: lead.deliveryTimeSlot || null,
    };
  };

  const closeEditModal = () => {
    setSelectedLead(null);
    setEditLeadModalVisible(false);
  };

  const onPressAddButton = () => {
    setAddLeadModalVisible(true);
  };

  const dropdownMenu = (row) => {
    return (
      <Menu>
        <Menu.Item onClick={() => showDrawer(row)}>
          <Flex alignItems="center">
            <EyeOutlined />
            <span style={{ marginLeft: 4 }}>{t("details")}</span>
          </Flex>
        </Menu.Item>
        <Menu.Item onClick={() => onPressEdit(row)}>
          <Flex alignItems="center">
            <EditOutlined />
            <span style={{ marginLeft: 4 }}>{t("edit")}</span>
          </Flex>
        </Menu.Item>
        <Menu.Item onClick={() => onPressConvertToCustomer(row)}>
          <Flex alignItems="center">
            <UserSwitchOutlined />
            <span style={{ marginLeft: 4 }}>{t("turn_into_customer")}</span>
          </Flex>
        </Menu.Item>
        <Menu.Item>
          <Popconfirm
            placement="bottom"
            title={t("confirm_delete_lead", { name: row.companyName })}
            onConfirm={() => deleteRow(row)}
            okText={t("yes")}
            cancelText={t("no")}
          >
            <a onClick={(e) => e.preventDefault}>
              <DeleteOutlined />
              {t("delete")}
            </a>
          </Popconfirm>
        </Menu.Item>
      </Menu>
    );
  };

  const deleteRow = async (row) => {
    try {
      await deleteDoc(doc(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_LEADS_TABLE, row.id));
      message.success(t("notifications.lead_deleted"), 4);
    } catch (error) {
      console.error("Error deleting lead:", error);
      message.error(error.message, 4);
    }
  };

  const bulkDeleteLeads = async () => {
    try {
      const deletePromises = selectedRowKeys.map((id) =>
        deleteDoc(doc(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_LEADS_TABLE, id)),
      );

      await Promise.all(deletePromises);

      message.success(t("notifications.leads_deleted", { count: selectedRowKeys.length }), 4);
      setSelectedRowKeys([]);
    } catch (error) {
      console.error("Error deleting leads:", error);
      message.error(t("notifications.error_deleting_leads"), 4);
    }
  };

  const openBulkDeliveryDateModal = () => {
    setBulkDeliveryDateModalVisible(true);
  };

  const closeBulkDeliveryDateModal = () => {
    setBulkDeliveryDateModalVisible(false);
    bulkDeliveryDateForm.resetFields();
  };

  const handleBulkSetDeliveryDate = async () => {
    if (selectedRowKeys.length === 0) return;

    try {
      const values = await bulkDeliveryDateForm.validateFields();
      const desiredDeliveryDate = values?.desiredDeliveryDate
        ? dayjs(values.desiredDeliveryDate).startOf("day").format("YYYY-MM-DD")
        : null;

      if (!desiredDeliveryDate) {
        message.error(t("notifications.please_select_delivery_date"));
        return;
      }

      message.loading({ content: t("notifications.updating_leads"), key: "bulkUpdate" });

      const updatePromises = selectedRowKeys.map((leadId) => {
        const leadDocRef = doc(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_LEADS_TABLE, leadId);
        return updateDoc(leadDocRef, {
          desiredDeliveryDate: desiredDeliveryDate,
        });
      });

      await Promise.all(updatePromises);

      message.destroy("bulkUpdate");
      message.success(t("notifications.bulk_delivery_date_updated", { count: selectedRowKeys.length }));

      closeBulkDeliveryDateModal();
    } catch (error) {
      message.destroy("bulkUpdate");
      if (error?.errorFields) {
        return;
      }
      console.error("Error updating delivery dates:", error);
      message.error(t("notifications.error_updating_delivery_dates"));
    }
  };

  const openBulkDeliverySettingsModal = () => {
    if (selectedRowKeys.length === 0) return;

    // Check if all selected leads are from the same country
    const selectedLeadsData = leads.filter((lead) => selectedRowKeys.includes(lead.id));
    const countries = [...new Set(selectedLeadsData.map((lead) => lead.country))];

    if (countries.length > 1) {
      message.error(t("notifications.leads_must_be_same_country"));
      return;
    }

    const country = countries[0] || COMPANY_COUNTRY_CODE;
    setSelectedLeadsCountry(country);
    setBulkDeliverySettingsModalVisible(true);
  };

  const closeBulkDeliverySettingsModal = () => {
    setBulkDeliverySettingsModalVisible(false);
    bulkDeliverySettingsForm.resetFields();
    setSelectedLeadsCountry(null);
  };

  const handleBulkSetDeliverySettings = async () => {
    if (selectedRowKeys.length === 0) return;

    try {
      const values = await bulkDeliverySettingsForm.validateFields();
      const deliveryLabel = values?.deliveryLabel || null;

      // Parse delivery time slot safely
      let deliveryTimeSlotObject = null;
      if (values.deliveryTimeSlot) {
        try {
          deliveryTimeSlotObject = JSON.parse(values.deliveryTimeSlot);
        } catch (e) {
          console.error("Error parsing time slot:", e);
          message.error(t("notifications.invalid_time_slot"));
          return;
        }
      }

      message.loading({ content: t("notifications.updating_leads"), key: "bulkUpdate" });

      const updatePromises = selectedRowKeys.map((leadId) => {
        const leadDocRef = doc(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_LEADS_TABLE, leadId);
        return updateDoc(leadDocRef, {
          deliveryLabel: deliveryLabel,
          deliveryTimeSlot: deliveryTimeSlotObject,
        });
      });

      await Promise.all(updatePromises);

      message.destroy("bulkUpdate");
      message.success(t("notifications.bulk_delivery_settings_updated", { count: selectedRowKeys.length }));

      closeBulkDeliverySettingsModal();
    } catch (error) {
      message.destroy("bulkUpdate");
      if (error?.errorFields) {
        return;
      }
      console.error("Error updating delivery settings:", error);
      message.error(t("notifications.error_updating_delivery_settings"));
    }
  };

  // Auto-update timeslot options when delivery label changes
  const getAvailableTimeSlotsForLabel = (deliveryLabelCode) => {
    if (!deliveryLabelCode || !selectedLeadsCountry) return [];

    const deliveryLabels = getCountryDeliveryLabels(selectedLeadsCountry);
    const labelConfig = deliveryLabels.find((dl) => dl.code === deliveryLabelCode);

    if (!labelConfig?.deliveryTimeSlots) return [];

    return labelConfig.deliveryTimeSlots.map((slot) => ({
      value: JSON.stringify({
        start: slot.start,
        end: slot.end,
        deliverySurcharge: Number(slot.deliverySurcharge || 0),
      }),
      label: `${slot.start} - ${slot.end}${slot.deliverySurcharge > 0 ? ` (+€${slot.deliverySurcharge})` : ""}`,
    }));
  };

  const openBulkShipmentsModal = async () => {
    if (selectedRowKeys.length === 0) return;

    // Get selected leads
    const selectedLeadsData = leads.filter((lead) => selectedRowKeys.includes(lead.id));

    // Validate each lead has required fields
    const invalidLeads = [];
    selectedLeadsData.forEach((lead) => {
      const errors = [];
      if (!lead.desiredDeliveryDate) {
        errors.push(t("desired_delivery_date"));
      }
      if (!lead.deliveryLabel) {
        errors.push(t("delivery_label"));
      }
      if (!lead.deliveryTimeSlot) {
        errors.push(t("time_slot"));
      }

      if (errors.length > 0) {
        invalidLeads.push({
          companyName: lead.companyName,
          errors: errors,
        });
      }
    });

    if (invalidLeads.length > 0) {
      Modal.error({
        title: t("notifications.invalid_leads_for_shipment"),
        content: (
          <div>
            <p>{t("notifications.following_leads_missing_required_fields")}</p>
            <ul style={{ margin: "8px 0", paddingLeft: "20px" }}>
              {invalidLeads.map((lead, index) => (
                <li key={index}>
                  <strong>{lead.companyName}</strong>: {lead.errors.join(", ")}
                </li>
              ))}
            </ul>
          </div>
        ),
        width: 600,
      });
      return;
    }

    // Check if any leads already have existing shipments (non-cancelled)
    try {
      const leadsWithShipments = [];

      for (const lead of selectedLeadsData) {
        const ordersQuery = query(
          collection(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_ORDERS_TABLE),
          where("leadId", "==", lead.leadId),
          where("type", "==", "acquisition"),
        );

        const ordersSnapshot = await getDocs(ordersQuery);

        // Check if any order is not cancelled
        const hasActiveShipment = ordersSnapshot.docs.some(
          (doc) => doc.data().farmorderStatus !== ORDER_STATUS_CANCELED,
        );

        if (hasActiveShipment) {
          leadsWithShipments.push(lead.companyName);
        }
      }

      if (leadsWithShipments.length > 0) {
        Modal.warning({
          title: t("notifications.leads_already_have_shipments"),
          content: (
            <div>
              <p>{t("notifications.following_leads_already_have_shipments")}</p>
              <ul style={{ margin: "8px 0", paddingLeft: "20px" }}>
                {leadsWithShipments.map((companyName, index) => (
                  <li key={index}>
                    <strong>{companyName}</strong>
                  </li>
                ))}
              </ul>
              <p style={{ marginTop: "12px" }}>{t("notifications.creating_duplicate_shipments_warning")}</p>
            </div>
          ),
          width: 600,
        });
        return;
      }
    } catch (error) {
      console.error("Error checking for existing shipments:", error);
      message.error(t("notifications.error_checking_existing_shipments"));
      return;
    }

    // All validations passed, open preview modal
    setBulkShipmentsLeads(selectedLeadsData);
    setBulkShipmentsModalVisible(true);
  };

  const closeBulkShipmentsModal = () => {
    setBulkShipmentsModalVisible(false);
    setBulkShipmentsLeads([]);
  };

  const handleBulkShipmentsSuccess = () => {
    // Add the newly created shipment leads to the leadsWithShipments Set
    const newLeadIds = bulkShipmentsLeads.map((lead) => lead.leadId);
    setLeadsWithShipments((prev) => {
      const updated = new Set(prev);
      newLeadIds.forEach((leadId) => updated.add(leadId));
      return updated;
    });

    closeBulkShipmentsModal();
  };

  const bulkActionsMenu = (
    <Menu>
      <Menu.Item key="set-delivery-date" onClick={openBulkDeliveryDateModal}>
        <Flex alignItems="center">
          <span>{t("set_delivery_date")}</span>
        </Flex>
      </Menu.Item>
      <Menu.Item key="set-delivery-settings" onClick={openBulkDeliverySettingsModal}>
        <Flex alignItems="center">
          <span>{t("set_delivery_settings")}</span>
        </Flex>
      </Menu.Item>
      <Menu.Item key="create-shipments" onClick={openBulkShipmentsModal}>
        <Flex alignItems="center">
          <span>{t("create_shipments")}</span>
        </Flex>
      </Menu.Item>
    </Menu>
  );

  const getLeadStatusColor = (status) => {
    switch (status) {
      case LEAD_STATUS_INTERESTED:
        return "green";
      case LEAD_STATUS_TRIAL_SENT:
        return "blue";
      case LEAD_STATUS_NOT_INTERESTED:
        return "red";
      case LEAD_STATUS_NO_ANSWER:
        return "default";
      case LEAD_STATUS_NEW:
        return "cyan";
      case LEAD_STATUS_FOLLOW_UP:
        return "orange";
      case LEAD_STATUS_OVERDUE:
        return "orange";
      case LEAD_STATUS_ACTION_NEEDED:
        return "orange";
      case LEAD_STATUS_READY:
        return "green";
      case LEAD_STATUS_MANUAL:
        return "purple";
      case LEAD_STATUS_ALMOST_IN:
        return "green";
      default:
        return "cyan";
    }
  };

  const handleDropFile = async (files) => {
    setIsDraggingExcelFile(false);

    if (files.length > 1) {
      message.error(t("notifications.one_file_at_a_time"));
      return;
    }

    const file = files[0];
    if (file.type !== "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") {
      message.error(t("notifications.upload_excel_file"));
      return;
    }

    try {
      message.loading({ content: t("processing_file"), key: "parseExcel" });

      const { validLeads, invalidLeads } = await parseExcelLeads(file, t);

      message.destroy("parseExcel");

      setPreviewValidLeads(validLeads);
      setPreviewInvalidLeads(invalidLeads);
      setPreviewModalVisible(true);

      if (invalidLeads.length > 0) {
        message.warning(`${invalidLeads.length} ${t("leads_have_errors").toLowerCase()}`);
      }
    } catch (error) {
      message.destroy("parseExcel");
      console.error("Error parsing Excel:", error);
      message.error(error.message || t("notifications.error_parsing_excel"));
    }
  };

  const handlePreviewModalSuccess = () => {
    setPreviewModalVisible(false);
    setPreviewValidLeads([]);
    setPreviewInvalidLeads([]);
  };

  const handlePreviewModalCancel = () => {
    setPreviewModalVisible(false);
    setPreviewValidLeads([]);
    setPreviewInvalidLeads([]);
  };

  const verifyLeadName = async (lead) => {
    try {
      const leadDocRef = doc(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_LEADS_TABLE, lead.id);

      await updateDoc(leadDocRef, { nameNeedsVerification: false });

      setFilteredLeads((prev) =>
        prev.map((item) => (item.id === lead.id ? { ...item, nameNeedsVerification: false } : item)),
      );

      message.success(t("notifications.lead_name_verified"));
    } catch (error) {
      console.error("Error verifying lead name:", error);
      message.error(t("notifications.error_verifying_lead_name"));
    }
  };

  const triggerLeadIntroMessage = async (lead) => {
    // TODO: temp
    return;
    if (!lead.phone) {
      return;
    }

    // Remove + from phone number for consistency
    const phoneNumber = lead.phone.replace(/^\+/, "");
    const timestamp = Math.floor(Date.now() / 1000);
    const { variables, variablesArray } = LEAD_INTRO_TEMPLATE.getVariables(lead);
    const renderedBody = LEAD_INTRO_TEMPLATE.render(variables);

    // Build contactName: "FirstName LastName - CompanyName"
    const firstName = lead.contactFirstName || "";
    const lastName = lead.contactLastName || "";
    const companyName = lead.companyName || "";

    const contactName = [`${firstName} ${lastName}`.trim(), companyName].filter(Boolean).join(" - ");

    const db = firestore;
    const chatSessionRef = doc(
      db,
      FIRESTORE_REGIONS_TABLE,
      COMPANY_COUNTRY_CODE,
      FIRESTORE_CHAT_SESSIONS_TABLE,
      phoneNumber,
    );

    try {
      // Step 1: Check if chat session exists
      const chatSession = await getDoc(chatSessionRef);

      if (chatSession.exists()) {
        message.warning(t("notifications.chat_session_already_exists"));
        return false;
      }

      // Create chat session with setDoc instead of updateDoc
      await setDoc(chatSessionRef, {
        recipientPhone: phoneNumber,
        contactName: contactName, // Now: "Twan Schmitz - Chef's Farm"
        createdAt: timestamp,
        updatedAt: timestamp,
        unreadMessageCount: 0,
        messageCount: 0,
        mode: "auto",
      });

      // Step 2: Create message in Firestore with rendered body
      const messageData = {
        messageType: "template",
        messageBody: renderedBody,
        type: "outgoing",
        createdAt: timestamp,
        senderId: "SYSTEM_AUTOMATION",
        messageNumber: 1,
        template: {
          id: LEAD_INTRO_TEMPLATE.id,
          name: LEAD_INTRO_TEMPLATE.name,
          language: LEAD_INTRO_TEMPLATE.language,
          variables,
          variablesArray,
        },
        timestamps: {
          whatsappSent: null,
          whatsappRead: null,
        },
      };

      const messageRef = await addDoc(
        collection(
          db,
          FIRESTORE_REGIONS_TABLE,
          COMPANY_COUNTRY_CODE,
          FIRESTORE_CHAT_SESSIONS_TABLE,
          phoneNumber,
          "messages",
        ),
        messageData,
      );
      const messageId = messageRef.id;

      // Step 3: Update chat session counts
      await updateDoc(chatSessionRef, {
        updatedAt: timestamp,
        messageCount: increment(1),
      });

      // Step 4: Add to outgoing queue
      const queueRef = collection(db, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, "outgoingWhatsappMessagesQueue");

      await addDoc(queueRef, {
        chatId: phoneNumber,
        messageId: messageId,
        leadId: lead.leadId,
        message: messageData,
        status: "pending",
        retryCount: 0,
        createdAt: timestamp,
        updatedAt: timestamp,
        log: [
          {
            timestamp: timestamp,
            action: "queued",
            description: "Message added to outgoing queue (manual to new trigger)",
          },
        ],
      });

      return true;
    } catch (error) {
      console.error(`Error in lead automation for ${lead.leadId}:`, error.message);
      throw error;
    }
  };

  const leadStatusMenu = (lead) => {
    // Filter statuses based on current status
    let availableStatuses = LEAD_STATUSES.filter((status) => status !== lead.status);

    // If current status is "manual", only show "new"
    if (lead.status === LEAD_STATUS_MANUAL) {
      availableStatuses = [LEAD_STATUS_NEW];
    }
    // If current status is NOT "manual", exclude "new" from options
    else {
      availableStatuses = availableStatuses.filter((status) => status !== LEAD_STATUS_NEW);
    }

    return (
      <Menu>
        {availableStatuses.map((status, key) => {
          return (
            <Menu.Item key={key}>
              <Popconfirm
                title={t("confirm_change_lead_status", {
                  lead: lead.companyName,
                  status: t(`lead_status.${status}`),
                })}
                onConfirm={() => updateLeadStatus(lead, status)}
                okText={t("yes")}
                cancelText={t("no")}
              >
                <a onClick={(e) => e.preventDefault()}>{t(`lead_status.${status}`)}</a>
              </Popconfirm>
            </Menu.Item>
          );
        })}
      </Menu>
    );
  };

  const updateLeadStatus = async (lead, status) => {
    try {
      const leadDocRef = doc(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_LEADS_TABLE, lead.id);

      // Check if transitioning from manual to new
      if (lead.status === LEAD_STATUS_MANUAL && status === LEAD_STATUS_NEW) {
        if (lead.nameNeedsVerification) {
          message.error(t("notifications.verify_lead_name_before_status_change"));
          return;
        }

        // TODO: Intro message functionality is disabled for now
        // When enabled, uncomment the code below to trigger WhatsApp intro message
        /*
        // Remove + from phone for chat session check
        const phoneNumber = lead.phone?.replace(/^\+/, "");

        if (!phoneNumber) {
          message.error(t("notifications.lead_has_no_phone"));
          return;
        }

        // Check if chat session already exists
        const chatSessionRef = doc(
          firestore,
          FIRESTORE_REGIONS_TABLE,
          COMPANY_COUNTRY_CODE,
          FIRESTORE_CHAT_SESSIONS_TABLE,
          phoneNumber,
        );
        const chatSessionDoc = await getDoc(chatSessionRef);

        if (chatSessionDoc.exists()) {
          message.warning(t("notifications.chat_session_already_exists"));
          return;
        }

        // Trigger the intro message flow
        message.loading({ content: t("notifications.sending_intro_message"), key: "sendIntro" });
        const success = await triggerLeadIntroMessage(lead);
        message.destroy("sendIntro");

        if (!success) {
          return;
        }

        message.success(t("notifications.intro_message_sent"));
        */
      }

      // Update the status
      await updateDoc(leadDocRef, { status });
      message.success(t("notifications.lead_status_updated"));
    } catch (error) {
      console.error("Error updating lead status:", error);
      message.error(t("notifications.lead_status_update_failed"));
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys) => {
      setSelectedRowKeys(selectedKeys);
    },
  };

  const tableColumns = [
    {
      title:
        selectedRowKeys.length > 0 ? (
          <span
            style={{ color: "#880e4f", cursor: "pointer" }}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedRowKeys([]);
            }}
            title="Clear selection"
          >
            ({selectedRowKeys.length})
          </span>
        ) : (
          ""
        ),
      render: (_, elm) => (
        <div className="text-right">
          <EllipsisDropdown menu={dropdownMenu(elm)} />
        </div>
      ),
    },
    {
      title: t("created_at"),
      dataIndex: "createdAt",
      defaultSortOrder: "descend",
      render: (_, data) => {
        const createdAtSeconds = getLeadCreatedAtSeconds(data);
        const showBadge = data.status === LEAD_STATUS_OVERDUE || data.status === LEAD_STATUS_FOLLOW_UP;
        if (!createdAtSeconds) return <span>-</span>;
        return (
          <div style={{ display: "flex" }}>
            {showBadge && <Badge status="warning" />}
            <button className={"text-button"} onClick={() => showDrawer(data)}>
              {dayjs.unix(createdAtSeconds).format("D MMM YYYY HH:mm")}
            </button>
          </div>
        );
      },
      sorter: (a, b) => {
        const aSeconds = getLeadCreatedAtSeconds(a);
        const bSeconds = getLeadCreatedAtSeconds(b);

        if (!aSeconds && !bSeconds) return 0;
        if (!aSeconds) return 1;
        if (!bSeconds) return -1;

        return aSeconds - bSeconds;
      },
      sortDirections: ["descend", "ascend"],
    },
    {
      title: t("company_name"),
      dataIndex: "companyName",
      sorter: (a, b) => utils.antdTableSorter(a, b, "companyName"),
      sortDirections: ["descend", "ascend"],
      render: (_, data) => {
        const countryConfig = getCountryDelivery(data.country);
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <DeliveryZoneBadge zipCode={data.zipCode} country={data.country} countryConfig={countryConfig} />
            <span>{data.companyName}</span>
          </div>
        );
      },
    },
    {
      title: t("contact_person"),
      dataIndex: "contactFirstName",
      render: (_, data) => {
        const name =
          data.contactFirstName && data.contactLastName
            ? `${data.contactFirstName} ${data.contactLastName}`
            : data.contactFirstName || data.contactLastName || "-";

        const needsVerification = data.nameNeedsVerification;

        return (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            {needsVerification && (
              <Popover
                overlayStyle={{ maxWidth: "30ch" }}
                content={
                  <>
                    <p style={{ marginBottom: 8 }}>{t("verify_name_description")}</p>
                    <Button
                      size="small"
                      type="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        verifyLeadName(data);
                      }}
                    >
                      {t("verify_name_button")}
                    </Button>
                  </>
                }
              >
                <Badge status="warning" style={{ cursor: "pointer" }} />
              </Popover>
            )}
            <span>{name}</span>
          </div>
        );
      },
      sorter: (a, b) => utils.antdTableSorter(a, b, "contactFirstName"),
    },
    {
      title: t("phone"),
      dataIndex: "phone",
      render: (data) => <span>{data || "-"}</span>,
    },
    {
      title: t("delivery_label"),
      dataIndex: "deliveryLabel",
      render: (_, data) => {
        const labelName = getDeliveryLabelName(data.deliveryLabel, data.country);
        return <span>{data.deliveryLabel || "-"}</span>;
      },
    },
    {
      title: t("time_slot"),
      dataIndex: "deliveryTimeSlot",
      render: (_, data) => {
        const timeSlotFormatted = formatTimeSlot(data.deliveryTimeSlot);
        return <span>{timeSlotFormatted || "-"}</span>;
      },
    },
    {
      title: t("desired_delivery_date"),
      dataIndex: "desiredDeliveryDate",
      render: (_, data) => {
        const deliveryDay = getLeadDesiredDeliveryDateDay(data);
        return <span>{deliveryDay ? deliveryDay.format("D MMM YYYY") : "-"}</span>;
      },
      sorter: (a, b) => {
        const aDay = getLeadDesiredDeliveryDateDay(a);
        const bDay = getLeadDesiredDeliveryDateDay(b);

        if (!aDay && !bDay) return 0;
        if (!aDay) return 1;
        if (!bDay) return -1;

        return aDay.unix() - bDay.unix();
      },
      sortDirections: ["descend", "ascend"],
    },
    {
      title: t("status"),
      dataIndex: "status",
      render: (_, lead) => (
        <span>
          <Dropdown overlay={leadStatusMenu(lead)} trigger={["click"]}>
            <a onClick={(e) => e.preventDefault()}>
              <Tag color={getLeadStatusColor(lead.status)}>{t(`lead_status.${lead.status}`)}</Tag>
            </a>
          </Dropdown>
        </span>
      ),
    },
    {
      title: "",
      render: (_, elm) => (
        <div className="text-right">
          <EllipsisDropdown menu={dropdownMenu(elm)} />
        </div>
      ),
    },
  ];

  const onSearch = (value) => {
    setSearchValue(value);
    applyFilters(
      value,
      selectedStatus,
      desiredDeliveryDateFilter,
      selectedCountry,
      hasDeliverySettings,
      hasShipmentFilter,
    );
  };

  const onStatusChange = (value) => {
    setSelectedStatus(value);
    applyFilters(
      searchValue,
      value,
      desiredDeliveryDateFilter,
      selectedCountry,
      hasDeliverySettings,
      hasShipmentFilter,
    );
  };

  const onDesiredDeliveryDateFilterChange = (date) => {
    setDesiredDeliveryDateFilter(date);
    applyFilters(searchValue, selectedStatus, date, selectedCountry, hasDeliverySettings, hasShipmentFilter);
  };

  const onCountryChange = (value) => {
    setSelectedCountry(value);
    applyFilters(searchValue, selectedStatus, desiredDeliveryDateFilter, value, hasDeliverySettings, hasShipmentFilter);
  };

  const onDeliverySettingsChange = (value) => {
    setHasDeliverySettings(value);
    applyFilters(searchValue, selectedStatus, desiredDeliveryDateFilter, selectedCountry, value, hasShipmentFilter);
  };

  const onShipmentFilterChange = (value) => {
    setHasShipmentFilter(value);
    applyFilters(searchValue, selectedStatus, desiredDeliveryDateFilter, selectedCountry, hasDeliverySettings, value);
  };

  const getUniqueCountries = () => {
    if (!leads) return [];
    const countries = [...new Set(leads.map((lead) => lead.country).filter(Boolean))];
    return countries.sort();
  };

  const applyFilters = (
    searchValue,
    statusFilter,
    desiredDeliveryDateFilter,
    countryFilter,
    deliverySettingsFilter,
    shipmentFilter,
  ) => {
    let filteredData = leads;

    // Apply status filter
    if (statusFilter && statusFilter !== "all") {
      filteredData = filteredData.filter((lead) => lead.status === statusFilter);
    }

    // Apply country filter
    if (countryFilter && countryFilter !== "all") {
      filteredData = filteredData.filter((lead) => lead.country === countryFilter);
    }

    // Apply delivery settings filter
    if (deliverySettingsFilter && deliverySettingsFilter !== "all") {
      if (deliverySettingsFilter === "with_settings") {
        filteredData = filteredData.filter((lead) => lead.deliveryLabel && lead.deliveryTimeSlot);
      } else if (deliverySettingsFilter === "without_settings") {
        filteredData = filteredData.filter((lead) => !lead.deliveryLabel || !lead.deliveryTimeSlot);
      }
    }

    // Apply shipment filter
    if (shipmentFilter && shipmentFilter !== "all") {
      if (shipmentFilter === "with_shipment") {
        filteredData = filteredData.filter((lead) => leadsWithShipments.has(lead.leadId));
      } else if (shipmentFilter === "without_shipment") {
        filteredData = filteredData.filter((lead) => !leadsWithShipments.has(lead.leadId));
      }
    }

    // Apply search
    if (searchValue) {
      filteredData = utils.wildCardSearch(filteredData, searchValue);
    }

    // Apply desired delivery date filter
    if (desiredDeliveryDateFilter) {
      const filterDay = dayjs(desiredDeliveryDateFilter).startOf("day");
      filteredData = filteredData.filter((lead) => {
        const leadDay = getLeadDesiredDeliveryDateDay(lead);
        if (!leadDay) return false;
        return leadDay.isSame(filterDay, "day");
      });
    }

    setFilteredLeads(filteredData);
  };

  const exportMenu = (
    <Menu>
      <Menu.Item key={"1"}>
        <span>{t("export_leads")}</span>
      </Menu.Item>
    </Menu>
  );

  return (
    <>
      <Row className={"mb-2"}>
        <Col xs={24} md={16} xl={15} xxl={14}>
          <h1>{t("leads")}</h1>
        </Col>
        <Col xs={24}>
          <Card>
            <Space
              wrap
              style={{ width: "100%", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}
            >
              {!isMobileView ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <Space wrap>
                    <Input
                      placeholder={t("search")}
                      prefix={<SearchOutlined />}
                      onChange={(e) => onSearch(e.target.value)}
                      allowClear
                      style={{ width: 200 }}
                    />
                  </Space>
                  <Space wrap>
                    <StyledSelect
                      placeholder={t("filter_by_status")}
                      value={selectedStatus}
                      onChange={onStatusChange}
                      style={{ width: 130 }}
                      $isFiltered={selectedStatus !== "all"}
                    >
                      <Select.Option value="all">{t("all_statuses")}</Select.Option>
                      {LEAD_STATUSES.map((status) => (
                        <Select.Option key={status} value={status}>
                          {t(`lead_status.${status}`)}
                        </Select.Option>
                      ))}
                    </StyledSelect>
                    <StyledSelect
                      placeholder={t("country")}
                      value={selectedCountry}
                      onChange={onCountryChange}
                      style={{ width: 130 }}
                      $isFiltered={selectedCountry !== "all"}
                    >
                      <Select.Option value="all">{t("all_countries")}</Select.Option>
                      {getUniqueCountries().map((country) => (
                        <Select.Option key={country} value={country}>
                          {country}
                        </Select.Option>
                      ))}
                    </StyledSelect>
                    <StyledSelect
                      placeholder={t("delivery_settings")}
                      value={hasDeliverySettings}
                      onChange={onDeliverySettingsChange}
                      style={{ width: 140 }}
                      $isFiltered={hasDeliverySettings !== "all"}
                    >
                      <Select.Option value="all">{t("delivery_set")}</Select.Option>
                      <Select.Option value="with_settings">{t("delivery_set_yes")}</Select.Option>
                      <Select.Option value="without_settings">{t("delivery_set_no")}</Select.Option>
                    </StyledSelect>
                    <StyledSelect
                      placeholder={t("shipment_status")}
                      value={hasShipmentFilter}
                      onChange={onShipmentFilterChange}
                      style={{ width: 140 }}
                      $isFiltered={hasShipmentFilter !== "all"}
                    >
                      <Select.Option value="all">{t("shipment_set")}</Select.Option>
                      <Select.Option value="with_shipment">{t("with_shipment")}</Select.Option>
                      <Select.Option value="without_shipment">{t("without_shipment")}</Select.Option>
                    </StyledSelect>
                    <DatePicker
                      placeholder={t("filter_by_desired_delivery_date")}
                      value={desiredDeliveryDateFilter}
                      onChange={onDesiredDeliveryDateFilterChange}
                      allowClear
                      format="DD-MM-YYYY"
                      style={{ width: 220 }}
                    />
                  </Space>
                </div>
              ) : (
                <Popover
                  content={
                    <FilterPopoverContent>
                      <div>
                        <FilterLabel>{t("search")}</FilterLabel>
                        <Input
                          placeholder={t("search")}
                          prefix={<SearchOutlined />}
                          onChange={(e) => onSearch(e.target.value)}
                          allowClear
                          size="large"
                        />
                      </div>
                      <div>
                        <FilterLabel>{t("filter_by_status")}</FilterLabel>
                        <Select
                          placeholder={t("filter_by_status")}
                          style={{ width: "100%" }}
                          value={selectedStatus}
                          onChange={onStatusChange}
                          size="large"
                        >
                          <Select.Option value="all">{t("all_statuses")}</Select.Option>
                          {LEAD_STATUSES.map((status) => (
                            <Select.Option key={status} value={status}>
                              {t(`lead_status.${status}`)}
                            </Select.Option>
                          ))}
                        </Select>
                      </div>
                      <div>
                        <FilterLabel>{t("country")}</FilterLabel>
                        <Select
                          placeholder={t("country")}
                          style={{ width: "100%" }}
                          value={selectedCountry}
                          onChange={onCountryChange}
                          size="large"
                        >
                          <Select.Option value="all">{t("all")}</Select.Option>
                          {getUniqueCountries().map((country) => (
                            <Select.Option key={country} value={country}>
                              {country}
                            </Select.Option>
                          ))}
                        </Select>
                      </div>
                      <div>
                        <FilterLabel>{t("delivery_settings")}</FilterLabel>
                        <Select
                          placeholder={t("delivery_settings")}
                          style={{ width: "100%" }}
                          value={hasDeliverySettings}
                          onChange={onDeliverySettingsChange}
                          size="large"
                        >
                          <Select.Option value="all">{t("all")}</Select.Option>
                          <Select.Option value="with_settings">{t("with_delivery_settings")}</Select.Option>
                          <Select.Option value="without_settings">{t("without_delivery_settings")}</Select.Option>
                        </Select>
                      </div>
                      <div>
                        <FilterLabel>{t("shipment_status")}</FilterLabel>
                        <Select
                          placeholder={t("shipment_status")}
                          style={{ width: "100%" }}
                          value={hasShipmentFilter}
                          onChange={onShipmentFilterChange}
                          size="large"
                        >
                          <Select.Option value="all">{t("all")}</Select.Option>
                          <Select.Option value="with_shipment">{t("with_shipment")}</Select.Option>
                          <Select.Option value="without_shipment">{t("without_shipment")}</Select.Option>
                        </Select>
                      </div>
                      <div>
                        <FilterLabel>{t("filter_by_desired_delivery_date")}</FilterLabel>
                        <DatePicker
                          placeholder={t("filter_by_desired_delivery_date")}
                          style={{ width: "100%" }}
                          value={desiredDeliveryDateFilter}
                          onChange={onDesiredDeliveryDateFilterChange}
                          allowClear
                          format="DD-MM-YYYY"
                          size="large"
                        />
                      </div>
                      <FilterButtonsContainer>
                        <Button
                          onClick={() => {
                            setSearchValue("");
                            setSelectedStatus("all");
                            setSelectedCountry("all");
                            setHasDeliverySettings("all");
                            setHasShipmentFilter("all");
                            setDesiredDeliveryDateFilter(null);
                            applyFilters("", "all", null, "all", "all", "all");
                            setFilterPopoverVisible(false);
                          }}
                          style={{ flex: 1 }}
                        >
                          {t("reset")}
                        </Button>
                        <Button type="primary" onClick={() => setFilterPopoverVisible(false)} style={{ flex: 1 }}>
                          {t("apply")}
                        </Button>
                      </FilterButtonsContainer>
                    </FilterPopoverContent>
                  }
                  title={t("filters")}
                  trigger="click"
                  open={filterPopoverVisible}
                  onOpenChange={setFilterPopoverVisible}
                  placement="bottomLeft"
                >
                  <Button
                    icon={<FilterOutlined />}
                    style={{
                      borderColor:
                        searchValue ||
                        selectedStatus !== "all" ||
                        hasShipmentFilter !== "all" ||
                        selectedCountry !== "all" ||
                        hasDeliverySettings !== "all" ||
                        desiredDeliveryDateFilter
                          ? "#FF4B33"
                          : "",
                      color:
                        searchValue ||
                        selectedStatus !== "all" ||
                        hasShipmentFilter !== "all" ||
                        selectedCountry !== "all" ||
                        hasDeliverySettings !== "all" ||
                        desiredDeliveryDateFilter
                          ? "#FF4B33"
                          : "",
                    }}
                  />
                </Popover>
              )}
              <div className={"d-flex"} style={{ gap: 12 }}>
                {selectedRowKeys.length > 0 && (
                  <>
                    <Dropdown overlay={bulkActionsMenu} trigger={["click"]}>
                      <Button>
                        {t("bulk_actions")} ({selectedRowKeys.length})
                      </Button>
                    </Dropdown>
                    <Popconfirm
                      title={t("confirm_delete_leads", { count: selectedRowKeys.length })}
                      onConfirm={bulkDeleteLeads}
                      okText={t("yes")}
                      cancelText={t("no")}
                    >
                      <Button danger icon={<DeleteOutlined />}>
                        {t("delete")} ({selectedRowKeys.length})
                      </Button>
                    </Popconfirm>
                  </>
                )}
                <Button type="primary" onClick={onPressAddButton}>
                  {t("add_lead")}
                </Button>
              </div>
            </Space>
            <div
              className="table-responsive"
              onDragEnter={(e) => {
                e.preventDefault();
                setIsDraggingExcelFile(true);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDraggingExcelFile(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                if (!e.currentTarget.contains(e.relatedTarget)) {
                  setIsDraggingExcelFile(false);
                }
              }}
            >
              <StyledDragger
                name="file"
                onDrop={(e) => handleDropFile(e.dataTransfer.files)}
                openFileDialogOnClick={false}
                customRequest={dummyRequest}
                fileList={[]}
                $isDragging={isDraggingExcelFile}
              >
                {isDraggingExcelFile && (
                  <UploadOverlayMessage>
                    <h2>{t("drag_and_drop_file_leads_title")}</h2>
                    <p>{t("drag_and_drop_file_leads_message")}</p>
                  </UploadOverlayMessage>
                )}
                <Table
                  showSorterTooltip={false}
                  columns={tableColumns}
                  dataSource={filteredLeads}
                  rowKey="id"
                  scroll={{ x: 1300 }}
                  loading={leadsLoading}
                  rowSelection={rowSelection}
                />
              </StyledDragger>
            </div>
          </Card>
        </Col>
      </Row>

      {selectedLead && (
        <Drawer
          title={t("lead_details", { lead: selectedLead.companyName })}
          placement="right"
          onClose={closeDrawer}
          open={drawerVisible}
          width={isMobile ? window.innerWidth : 850}
          zIndex={1000}
        >
          <Descriptions
            bordered
            title={
              <div className="flex-row">
                {t("lead_information")}{" "}
                <Tag color={getLeadStatusColor(selectedLead.status)}>{t(`lead_status.${selectedLead.status}`)}</Tag>
              </div>
            }
            size="small"
            column={1}
          >
            <Descriptions.Item label={t("lead_id")}>
              {profile?.userRole === roles.superAdmin ? (
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    const win = window.open("", "_blank");
                    win.document.write(`<pre>${JSON.stringify(selectedLead, null, 2)}</pre>`);
                    win.document.close();
                  }}
                  style={{ color: "rgba(0, 0, 0, 0.88)", cursor: "default" }}
                >
                  {selectedLead.leadId}
                </a>
              ) : (
                selectedLead.leadId
              )}
            </Descriptions.Item>
            <Descriptions.Item label={t("company_name")}>{selectedLead.companyName}</Descriptions.Item>
            <Descriptions.Item label={t("contact_person")}>
              {selectedLead.contactFirstName && selectedLead.contactLastName
                ? `${selectedLead.contactFirstName} ${selectedLead.contactLastName}`
                : selectedLead.contactFirstName || selectedLead.contactLastName || "-"}
            </Descriptions.Item>
            <Descriptions.Item label={t("email")}>{selectedLead.email}</Descriptions.Item>
            <Descriptions.Item label={t("phone")}>{selectedLead.phone || "-"}</Descriptions.Item>
            <Descriptions.Item label={t("address")}>{selectedLead.address}</Descriptions.Item>
            <Descriptions.Item label={t("city")}>{selectedLead.city}</Descriptions.Item>
            <Descriptions.Item label={t("country")}>{selectedLead.country}</Descriptions.Item>
            <Descriptions.Item label={t("website")}>{selectedLead.website || "-"}</Descriptions.Item>
            <Descriptions.Item label={t("company_type")}>{selectedLead.companyType || "-"}</Descriptions.Item>
            <Descriptions.Item label={t("origin")}>{selectedLead.origin || "-"}</Descriptions.Item>
            <Descriptions.Item label={t("delivery_label")}>
              {selectedLead.deliveryLabel
                ? getDeliveryLabelName(selectedLead.deliveryLabel, selectedLead.country)
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label={t("delivery_time_slot")}>
              {selectedLead.deliveryTimeSlot ? formatTimeSlot(selectedLead.deliveryTimeSlot) : "-"}
            </Descriptions.Item>
            <Descriptions.Item label={t("delivery_instructions")}>
              {selectedLead.deliveryInstructions || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Delivery Instructions (PDF)">
              {selectedLead.deliveryInstructionsUrl ? (
                <Space direction="vertical">
                  <a href={selectedLead.deliveryInstructionsUrl} target="_blank" rel="noreferrer">
                    Delivery Instructions (PDF)
                  </a>
                  <Popconfirm
                    title="Remove Delivery Instructions?"
                    okText={t("delete")}
                    cancelText={t("close")}
                    onConfirm={handleRemoveLeadDeliveryInstructions}
                  >
                    <Button danger size="small">
                      Remove
                    </Button>
                  </Popconfirm>
                </Space>
              ) : (
                "-"
              )}
            </Descriptions.Item>
          </Descriptions>
          <ButtonsWrapper>
            <Button onClick={() => onPressEdit(selectedLead)}>{t("edit_lead")}</Button>
            <Button type="primary" icon={<UserSwitchOutlined />} onClick={() => onPressConvertToCustomer(selectedLead)}>
              {t("turn_into_customer")}
            </Button>
          </ButtonsWrapper>

          <Descriptions
            bordered
            title={t("follow_up_information")}
            size="small"
            column={1}
            style={{ marginTop: "1rem" }}
            loading={loadingLeadOrder}
          >
            <Descriptions.Item label={t("desired_delivery_date")}>
              {getLeadDesiredDeliveryDateDay(selectedLead)
                ? getLeadDesiredDeliveryDateDay(selectedLead).format("DD-MM-YYYY")
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label={t("trial_package_sent")}>
              {leadOrder?.orderDateTime ? dayjs(leadOrder.orderDateTime).format("DD-MM-YYYY HH:mm") : "-"}
            </Descriptions.Item>
            <Descriptions.Item label={t("delivered_at")}>
              {leadOrder?.deliveredAt ? dayjs(leadOrder.deliveredAt).format("DD-MM-YYYY HH:mm") : "-"}
            </Descriptions.Item>
            <Descriptions.Item label={t("can_be_called_from")}>
              {leadOrder?.deliveredAt ? dayjs(leadOrder.deliveredAt).add(7, "days").format("DD-MM-YYYY") : "-"}
            </Descriptions.Item>
            <Descriptions.Item label={t("internal_notes")}>{selectedLead.notes || "-"}</Descriptions.Item>
            <Descriptions.Item label={t("chat_summary")}>
              {selectedLead.shipmentInfo?.chatSummary || "-"}
            </Descriptions.Item>
            <Descriptions.Item label={t("created_at")}>
              {selectedLead.createdAt ? dayjs.unix(selectedLead.createdAt).format("DD-MM-YYYY HH:mm") : "-"}
            </Descriptions.Item>
            <Descriptions.Item label={t("created_by")}>{selectedLead.createdBy || "-"}</Descriptions.Item>
            <Descriptions.Item label={t("updated_at")}>
              {selectedLead.updatedAt ? dayjs.unix(selectedLead.updatedAt).format("DD-MM-YYYY HH:mm") : "-"}
            </Descriptions.Item>
            <Descriptions.Item label={t("updated_by")}>{selectedLead.updatedBy || "-"}</Descriptions.Item>
          </Descriptions>
          <ButtonsWrapper>
            <Button onClick={openEditFollowUpModal}>{t("edit_follow_up_information")}</Button>
          </ButtonsWrapper>

          <div style={{ marginTop: "1rem", marginBottom: "2rem" }}>
            <Typography.Title level={5} style={{ marginBottom: "1rem" }}>
              {t("log")}
            </Typography.Title>
            {selectedLead?.log && selectedLead.log.length > 0 ? (
              <div style={{ maxHeight: "400px", overflowY: "auto", padding: "1rem" }}>
                <Timeline mode="left">
                  {selectedLead.log.map((logEntry, index) => {
                    const isError = logEntry.type === "address_invalid" || logEntry.type === "error";
                    const isSuccess = logEntry.type === "success" || logEntry.type === "shipment_confirmed";

                    let color = "gray";
                    if (isError) color = "red";
                    else if (isSuccess) color = "green";
                    else if (logEntry.statusChange) color = "blue";

                    return (
                      <Timeline.Item key={index} color={color}>
                        <div>
                          <Typography.Text strong>
                            {dayjs.unix(logEntry.timestamp).format("DD-MM-YYYY HH:mm")}
                          </Typography.Text>
                          {logEntry.statusChange && (
                            <div>
                              <Tag color="blue" style={{ marginTop: "0.25rem" }}>
                                {logEntry.statusChange}
                              </Tag>
                            </div>
                          )}
                          <div style={{ marginTop: "0.5rem" }}>
                            <Typography.Text>{logEntry.description}</Typography.Text>
                          </div>
                          {logEntry.function && (
                            <div style={{ marginTop: "0.25rem" }}>
                              <Typography.Text type="secondary" style={{ fontSize: "12px" }}>
                                {logEntry.function}
                              </Typography.Text>
                            </div>
                          )}
                          {logEntry.createdBy && (
                            <div style={{ marginTop: "0.25rem" }}>
                              <Typography.Text type="secondary" style={{ fontSize: "12px" }}>
                                {t("created_by")}: {logEntry.createdBy}
                              </Typography.Text>
                            </div>
                          )}
                        </div>
                      </Timeline.Item>
                    );
                  })}
                </Timeline>
              </div>
            ) : (
              <Typography.Text type="secondary">{t("no_log_entries")}</Typography.Text>
            )}
          </div>
        </Drawer>
      )}

      <PreviewExcelLeadsModal
        visible={previewModalVisible}
        onSuccess={handlePreviewModalSuccess}
        onCancel={handlePreviewModalCancel}
        validLeads={previewValidLeads}
        invalidLeads={previewInvalidLeads}
      />

      <AddLeadModal
        visible={addLeadModalVisible}
        onClose={() => setAddLeadModalVisible(false)}
        mode="add"
        allLeads={leads}
      />

      <AddLeadModal
        visible={editLeadModalVisible}
        onClose={closeEditModal}
        mode="edit"
        initialValues={selectedLead}
        allLeads={leads}
      />

      <Modal
        title={t("edit_follow_up_information")}
        open={editFollowUpModalVisible}
        onCancel={closeEditFollowUpModal}
        onOk={handleSaveFollowUpInformation}
        okText={t("save")}
        cancelText={t("close")}
        destroyOnClose
        centered
        zIndex={1200}
      >
        <Form form={editFollowUpForm} layout="vertical">
          <Form.Item name="desiredDeliveryDate" label={t("desired_delivery_date")}>
            <DatePicker style={{ width: "100%" }} format="DD-MM-YYYY" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t("set_delivery_date_for_leads", { count: selectedRowKeys.length })}
        open={bulkDeliveryDateModalVisible}
        onCancel={closeBulkDeliveryDateModal}
        onOk={handleBulkSetDeliveryDate}
        okText={t("save")}
        cancelText={t("cancel")}
        destroyOnClose
        centered
      >
        <Form form={bulkDeliveryDateForm} layout="vertical">
          <Form.Item
            name="desiredDeliveryDate"
            label={t("desired_delivery_date")}
            rules={[{ required: true, message: t("please_select_delivery_date") }]}
          >
            <DatePicker style={{ width: "100%" }} format="DD-MM-YYYY" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t("set_delivery_settings_for_leads", { count: selectedRowKeys.length })}
        open={bulkDeliverySettingsModalVisible}
        onCancel={closeBulkDeliverySettingsModal}
        onOk={handleBulkSetDeliverySettings}
        okText={t("save")}
        cancelText={t("cancel")}
        destroyOnClose
        centered
      >
        {selectedLeadsCountry && (
          <Typography.Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
            {t("country")}: <strong>{selectedLeadsCountry}</strong>
          </Typography.Text>
        )}
        <Form form={bulkDeliverySettingsForm} layout="vertical">
          <Form.Item
            name="deliveryLabel"
            label={t("delivery_label")}
            rules={[{ required: true, message: t("select_delivery_label") }]}
          >
            <Select placeholder={t("delivery_label_placeholder")} allowClear style={{ width: "100%" }}>
              {selectedLeadsCountry &&
                getCountryDeliveryLabels(selectedLeadsCountry).map((label) => (
                  <Select.Option key={label.code} value={label.code}>
                    {label.label}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="deliveryTimeSlot"
            label={t("delivery_time_slot")}
            rules={[{ required: true, message: t("select_delivery_time_slot") }]}
          >
            <Select
              placeholder={t("select_delivery_time_slot")}
              allowClear
              style={{ width: "100%" }}
              disabled={!deliveryLabelWatch}
            >
              {deliveryLabelWatch &&
                getAvailableTimeSlotsForLabel(deliveryLabelWatch).map((slot) => (
                  <Select.Option key={slot.value} value={slot.value}>
                    {slot.label}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      <AddCompanyModal
        visible={convertToCustomerModalVisible}
        onClose={closeConvertToCustomerModal}
        exactCustomers={[]}
        prefillValues={leadToConvert ? buildCustomerPrefillFromLead(leadToConvert) : null}
        onSuccess={onConvertSuccess}
      />

      <PreviewBulkShipmentsModal
        visible={bulkShipmentsModalVisible}
        onCancel={closeBulkShipmentsModal}
        onSuccess={handleBulkShipmentsSuccess}
        leads={bulkShipmentsLeads}
        products={products}
      />
    </>
  );
};

const ButtonsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 1rem;
`;

const UploadOverlayMessage = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(2px);
  z-index: 2;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease-in-out;
`;

const StyledDragger = styled(Dragger)`
  .ant-upload-drag {
    cursor: default !important;
    border: none !important;
    border: ${(props) => (props.$isDragging ? "2px dashed #40a9ff" : "none")} !important;
    background: ${(props) => (props.$isDragging ? "#f0f8ff" : "transparent")} !important;
    transition: all 0.2s ease-in-out;

    .ant-upload {
      padding: 0 !important;
      cursor: default !important;
    }

    &:hover {
      border: ${(props) => (props.$isDragging ? "2px dashed #40a9ff" : "none")} !important;
      background: ${(props) => (props.$isDragging ? "#f0f8ff" : "transparent")} !important;
    }
  }
`;

const FilterPopoverContent = styled.div`
  width: 300px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FilterLabel = styled.label`
  display: block;
  margin-bottom: 4px;
  font-weight: 500;
`;

const FilterButtonsContainer = styled.div`
  display: flex;
  gap: 8px;
  padding-top: 4px;
  border-top: 1px solid #f0f0f0;
  margin-top: 4px;
`;

export default Leads;
